// apps/web/src/components/finance/FinanceDashboard.tsx
// Finance dashboard UI component

import { useState, useEffect } from 'react';
import type { Invoice } from '../../types/invoice';
import type { Payment } from '../../types/payment';
import { apiClient } from '../../lib/apiClient';

export function FinanceDashboard() {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchFinanceData = async () => {
      try {
        setLoading(true);
        
        // Fetch invoices
        const invoicesResponse = await apiClient.get('/api/v1/finance/invoices');
        setInvoices(invoicesResponse.data.invoices);
        
        // Fetch payments
        const paymentsResponse = await apiClient.get('/api/v1/finance/payments');
        setPayments(paymentsResponse.data.payments);
      } catch (err) {
        setError('Failed to load finance data');
        console.error('Error loading finance data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFinanceData();
  }, []);

  if (loading) {
    return <div className="loading">Loading finance data...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  // Calculate totals
  const totalInvoices = invoices.reduce((sum, invoice) => sum + invoice.amount, 0);
  const totalPaid = invoices
    .filter(inv => inv.status === 'PAID')
    .reduce((sum, invoice) => sum + invoice.amount, 0);
  
  const totalPayments = payments.reduce((sum, payment) => sum + payment.amount, 0);

  return (
    <div className="finance-dashboard">
      <h1>Finance Dashboard</h1>
      
      <div className="finance-stats">
        <div className="stat-card">
          <h3>${totalInvoices.toLocaleString()}</h3>
          <p>Total Invoices</p>
        </div>
        <div className="stat-card">
          <h3>${totalPaid.toLocaleString()}</h3>
          <p>Paid Invoices</p>
        </div>
        <div className="stat-card">
          <h3>${totalPayments.toLocaleString()}</h3>
          <p>Total Payments</p>
        </div>
        <div className="stat-card">
          <h3>${(totalInvoices - totalPaid).toLocaleString()}</h3>
          <p>Pending Payments</p>
        </div>
      </div>

      <div className="finance-sections">
        <div className="section">
          <h2>Invoices</h2>
          <div className="invoices-grid">
            {invoices.map(invoice => (
              <InvoiceCard key={invoice.id} invoice={invoice} />
            ))}
          </div>
        </div>
        
        <div className="section">
          <h2>Payments</h2>
          <div className="payments-grid">
            {payments.map(payment => (
              <PaymentCard key={payment.id} payment={payment} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper component
function InvoiceCard({ invoice }: { invoice: Invoice }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'status-draft';
      case 'SENT': return 'status-sent';
      case 'PAID': return 'status-paid';
      case 'OVERDUE': return 'status-overdue';
      case 'CANCELLED': return 'status-cancelled';
      default: return 'status-default';
    }
  };

  return (
    <div className="invoice-card">
      <div className="invoice-header">
        <h3>INV-{invoice.id.substring(0, 6)}</h3>
        <span className={`status-badge ${getStatusColor(invoice.status)}`}>
          {invoice.status}
        </span>
      </div>
      <p><strong>Amount:</strong> ${invoice.amount.toLocaleString()}</p>
      <p><strong>Due:</strong> {invoice.dueDate}</p>
      <p><strong>Client:</strong> {invoice.clientId.substring(0, 8)}...</p>
      <p><strong>Description:</strong> {invoice.description || 'No description'}</p>
      <div className="actions">
        {invoice.status === 'DRAFT' && (
          <button className="btn btn-success">Send Invoice</button>
        )}
        {invoice.status === 'SENT' && (
          <button className="btn btn-primary">Mark Paid</button>
        )}
        <button className="btn btn-secondary">View</button>
      </div>
    </div>
  );
}

// Helper component
function PaymentCard({ payment }: { payment: Payment }) {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PENDING': return 'status-pending';
      case 'PROCESSING': return 'status-processing';
      case 'COMPLETED': return 'status-completed';
      case 'FAILED': return 'status-failed';
      case 'REFUNDED': return 'status-refunded';
      default: return 'status-default';
    }
  };

  return (
    <div className="payment-card">
      <div className="payment-header">
        <h3>PAY-{payment.id.substring(0, 6)}</h3>
        <span className={`status-badge ${getStatusColor(payment.status)}`}>
          {payment.status}
        </span>
      </div>
      <p><strong>Amount:</strong> ${payment.amount.toLocaleString()}</p>
      <p><strong>Method:</strong> {payment.paymentMethod}</p>
      <p><strong>Reference:</strong> {payment.reference || 'N/A'}</p>
      <p><strong>Date:</strong> {new Date().toLocaleDateString()}</p>
      <div className="actions">
        {payment.status === 'PENDING' && (
          <button className="btn btn-success">Process Payment</button>
        )}
        {payment.status === 'COMPLETED' && (
          <button className="btn btn-warning">Refund</button>
        )}
        <button className="btn btn-secondary">View</button>
      </div>
    </div>
  );
}